/**
 * useAIStream — transport + parse hook for the AI assistant chat.
 *
 * Extracted from AIAssistant.jsx (sprint F2 T5). Encapsulates everything that
 * is NOT state management: the fetch to /api/functions/invoke, the SSE reader,
 * the JSON fallback, the AbortController, the three timers (28s global abort,
 * 25s inter-chunk, 15s slow-query toast) and the analytics events.
 *
 * The parent stays in charge of `messages`, `pendingActions`, `chart` state —
 * this hook only forwards parsed events via callbacks. That lets the hook be
 * unit-tested in isolation (mock fetch + ReadableStream) without needing a
 * React tree or the message model.
 *
 * Contract:
 *   const { stream, abort, isStreaming } = useAIStream();
 *
 *   await stream({
 *     message,
 *     params,             // full body.params payload for /api/functions/invoke
 *     analyticsContext,   // extra fields merged into every track() call
 *     callbacks: {
 *       onText(deltaText) {},
 *       onRouting({ domain, confidence, crossDomains }) {},
 *       onPlan(steps) {},                 // string[] — multi-step plan (multi-domain turns)
 *       onThinkingText(value) {},         // supervisor reasoning narration (string)
 *       onAgentStart({ agent, domain }) {},
 *       onAgentFinish({ agent, summary, error, tools, tier, costEur, citations }) {},
 *       onToolThinking(toolName, fullText, domain) {},
 *       onChart(chartData) {},
 *       onGroundingWarning({ figures }) {},  // D-1 — cifras (€) no verificadas contra los datos
 *       onConversationId(id) {},
 *       onUsage(usage) {},
 *       onDone({ toolsUsed, agent, chart, pendingActions, path }) {},
 *       onError({ isTimeout, message, status }) {},
 *     },
 *   });
 *
 * All callbacks are optional — missing ones are treated as no-ops.
 */

import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { track } from '@/lib/analytics';
import { captureLog } from '@/lib/errorReporter';
import { createAIEventSink, conReferencia } from './aiEventSink';
import { openPlanLimit } from '@/stores/planLimitStore';
const __LOG_CTX = { context: 'src/components/ai/hooks/useAIStream' };

// Streaming endpoint goes through CloudFront → Lambda Function URL with InvokeMode
// RESPONSE_STREAM (verix-api-stream-dev), Lambda timeout = 60s. Cross-domain
// queries (HR + projects + finance) regularly need 30-40s end-to-end. Aligning
// the abort to ~55s gives the Lambda its full budget while keeping a 5s safety
// margin for the network round-trip / SSE close.
//
// IMPORTANT: the Lambda stream handler currently BUFFERS the full response and
// writes it in one shot at the end (progressive chunk-by-chunk delivery through
// CloudFront proved unreliable — it forwarded the first chunk then withheld the
// rest). So the client receives nothing until the very end, then the whole SSE
// body at once. INTER_CHUNK_TIMEOUT_MS therefore caps the wait for the FIRST
// chunk and must comfortably exceed the worst-case Lambda duration (must stay
// below ABORT). When progressive streaming is fixed, this can drop to ~20s.
const ABORT_TIMEOUT_MS       = 55_000; // Lambda Function URL hard cap is 60s
const INTER_CHUNK_TIMEOUT_MS = 50_000; // Buffered: first chunk arrives only at the end
const SLOW_QUERY_TOAST_MS    = 15_000; // UX hint — "your query is taking longer than usual"

export default function useAIStream() {
  const abortRef  = useRef(null);
  const readerRef = useRef(null);
  const requestIdRef = useRef(0);
  const [isStreaming, setIsStreaming] = useState(false);

  const abort = useCallback(() => {
    requestIdRef.current += 1;
    try { readerRef.current?.cancel(); } catch { /* ignore */ }
    try { abortRef.current?.abort(); } catch { /* ignore */ }
    readerRef.current = null;
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const stream = useCallback(async ({ message, params, analyticsContext = {}, callbacks = {} }) => {
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestIdRef.current === requestId;
    callbacks = Object.fromEntries(Object.entries(callbacks).map(([name, callback]) => [name, (...args) => isCurrent() ? callback?.(...args) : undefined]));
    const {
      // El resto de callbacks los reenvía `createAIEventSink` desde el propio
      // `callbacks`; aquí sólo se desestructuran los que usa este transporte
      // fuera del sink (rama JSON, errores de red, cierre del stream).
      onText, onChart, onConversationId, onUsage, onDone, onError, onHrOptInRequired,
    } = callbacks;

    const startedAt = Date.now();
    setIsStreaming(true);

    // Analytics — entry event
    track('ai.message.sent', {
      ...analyticsContext,
      message_len: typeof message === 'string' ? message.length : 0,
    });

    // Lazy-load apiFetch internals so we can stay outside the wrapper that
    // forces JSON parsing. We still need its base URL + CSRF + rate limiter.
    const { API_BASE: apiBase, getApiHeaders, checkInvokeRateLimit } = await import('@/api/apiFetch');
    if (!isCurrent()) return;
    if (!checkInvokeRateLimit()) {
      // Sprint 2 F6 — surface rate-limit explicitly. The previous error was
      // generic ("Rate limit exceeded — too many AI calls") and the caller
      // showed it as "Lo siento, no pude procesar tu consulta", which is
      // misleading. A toast here gives the user actionable info regardless
      // of how the parent handles onError.
      toast.error(i18n.t('ai.stream.rate_limit', 'Has hecho demasiadas consultas a Aria. Espera un minuto antes de volver a preguntar.'), { duration: 8_000 });
      const err = new Error('Has hecho demasiadas consultas a Aria. Espera un minuto.');
      err.code = 'rate_limited';
      err.status = 429;
      track('ai.error', { ...analyticsContext, duration_ms: Date.now() - startedAt, error_code: 'rate_limited' });
      onError?.({ isTimeout: false, message: err.message, status: 429 });
      setIsStreaming(false);
      throw err;
    }

    // 28s global AbortController + timer — caps the whole request end-to-end.
    const controller = new AbortController();
    abortRef.current = controller;
    const globalTimeoutId = setTimeout(() => controller.abort(), ABORT_TIMEOUT_MS);

    // Slow-query toast — dismissed on first chunk / done / error.
    let slowToastId = null;
    const slowTimer = setTimeout(() => {
      if (!isCurrent()) return;
      slowToastId = toast.info(i18n.t('ai.stream.slow_query', 'La consulta está tardando más de lo habitual…'), {
        description: i18n.t('ai.stream.slow_query_desc', 'Seguimos procesando. Puedes cancelar con el botón si lo necesitas.'),
        duration: 20_000,
      });
    }, SLOW_QUERY_TOAST_MS);
    const clearSlowToast = () => {
      clearTimeout(slowTimer);
      if (slowToastId !== null) {
        try { toast.dismiss(slowToastId); } catch { /* best-effort */ }
        slowToastId = null;
      }
    };

    let interChunkTimeout;
    try {
      // Streaming endpoint: CloudFront (OAC → Lambda Function URL, RESPONSE_STREAM)
      // in front of the stream Lambda. Path is always /api/functions/invoke (same
      // Express route as the main API). VITE_AI_STREAM_URL is the base of the
      // dedicated stream host (e.g. https://stream.dev.verix.es); local dev falls
      // back to the regular apiBase.
      const streamBase = import.meta.env.VITE_AI_STREAM_URL || apiBase;
      const aiEndpoint = streamBase.replace(/\/$/, '') + '/api/functions/invoke';
      // El backend normaliza el texto desde params.prompt|message|text
      // (router.ts) y aiChatSchema exige message. Si el caller pasó el texto
      // solo en el `message` de nivel superior (analítica) y no en params, el
      // body saldría sin consulta → 400 "Invalid chat request". Lo inyectamos
      // aquí, sin pisar a los callers que ya lo ponen en params.
      const bodyParams = { ...params };
      if (message && bodyParams.prompt == null && bodyParams.message == null && bodyParams.text == null) {
        bodyParams.prompt = message;
        bodyParams.message = message;
      }
      const body = JSON.stringify({ function: 'aiOrchestrator', params: bodyParams });

      const headers = getApiHeaders();
      // CloudFront OAC signs the origin request to the Lambda Function URL. Lambda
      // function URLs reject unsigned payloads on POST, so the viewer must supply
      // the body's SHA-256 in x-amz-content-sha256 for CloudFront to sign the
      // correct payload hash (otherwise: InvalidSignatureException). Only needed
      // when going through the CloudFront stream host — the local proxy / API
      // Gateway path ignores it.
      if (import.meta.env.VITE_AI_STREAM_URL && globalThis.crypto?.subtle) {
        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(body));
        headers['x-amz-content-sha256'] = Array.from(new Uint8Array(digest))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
      }

      if (!isCurrent()) return;
      const res = await fetch(aiEndpoint, {
        method: 'POST',
        credentials: 'include',
        headers,
        body,
        signal: controller.signal,
      });
      clearTimeout(globalTimeoutId);
      if (!isCurrent()) return;

      const contentType = res.headers.get('content-type') || '';

      if (contentType.includes('text/event-stream')) {
        const reader  = res.body.getReader();
        readerRef.current = reader;
        const decoder = new TextDecoder();
        let buffer = '';
        // El intérprete de eventos es COMPARTIDO con el run durable
        // (`useAriaRun`): mismo objeto, distinto transporte. Ver `aiEventSink.js`.
        // Acumula fullText, toolsUsed, chart, pendingActions, sawDone y el error
        // reintentable; aquí sólo queda lo que es propio del transporte SSE.
        // `onPlanLimit` se inyecta aqui y no lo pide cada consumidor: el tope de plan
        // tiene que abrir el mismo modal SIEMPRE, igual que `query-client.ts` lo hace
        // para todas las mutaciones. Si el llamador trae el suyo, manda el suyo.
        const sink = createAIEventSink({ onPlanLimit: openPlanLimit, ...callbacks });
        // B2 — cuenta de líneas SSE descartadas por JSON inválido; si son muchas
        // sin evento final, la respuesta llegó corrupta y avisamos al usuario.
        let parseErrors = 0;
        // Diagnóstico temporal: capturar el primer chunk crudo para correlacionar
        // con la telemetría del backend. Si backend reporta text_newlines>0 pero
        // aquí firstChunk no contiene "\\n" en el JSON, el problema está en el
        // transporte (Lambda buffer / API Gateway / encoder).
        let firstChunkLogged = false;

        // Inter-chunk watchdog — cancel reader if the stream stalls.
        const resetInterChunk = () => {
          clearTimeout(interChunkTimeout);
          interChunkTimeout = setTimeout(() => {
            try { reader.cancel(); } catch { /* ignore */ }
          }, INTER_CHUNK_TIMEOUT_MS);
        };
        resetInterChunk();

        while (true) {
          const { done, value } = await reader.read();
          if (!isCurrent()) return;
          if (done) { clearSlowToast(); break; }
          clearSlowToast();
          resetInterChunk();
          const chunk = decoder.decode(value, { stream: true });
          if (!firstChunkLogged && chunk.length > 0) {
            firstChunkLogged = true;
            // Sample del primer chunk para diagnosticar pérdida de \n.
            // \\n cuenta apariciones del literal "\\n" (escape JSON, lo que
            // esperamos en SSE); \n cuenta saltos de línea reales (separadores
            // de eventos SSE).
            captureLog('info', __LOG_CTX, '[useAIStream] first chunk sample', {
              len: chunk.length,
              real_newlines: (chunk.match(/\n/g) || []).length,
              json_escaped_newlines: (chunk.match(/\\n/g) || []).length,
              first_120: chunk.slice(0, 120),
            });
          }
          buffer += chunk;

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              sink.apply(JSON.parse(line.slice(6)));
            } catch (err) {
              parseErrors += 1;
              captureLog('warn', __LOG_CTX, '[useAIStream] SSE parse error:', err.message, line.slice(0, 100));
            }
          }
        }

        clearTimeout(interChunkTimeout);

        // B2 — error reintentable del backend: no lo presentamos como respuesta,
        // delegamos en onError con retryable=true para que el componente muestre
        // el botón "Reintentar".
        if (sink.state.retryableError) {
          clearSlowToast();
          track('ai.error', {
            ...analyticsContext,
            duration_ms: Date.now() - startedAt,
            error_code: 'ai_unavailable',
            path: 'sse',
          });
          onError?.({ isTimeout: false, message: sink.state.retryableError.message, status: 503, retryable: true });
          return;
        }

        // Sprint 2 F7 — surface truncation. If we got bytes but no terminal
        // event, the response is incomplete — warn the user rather than
        // presenting it as final. B2 — also when many SSE lines failed to parse.
        const { fullText, toolsUsed, agentMeta, chart: chartData, groundingWarning, pendingActions: receivedActions, pendingClarifications: receivedClarifications, sawDone } = sink.state;

        // XC-AI-013 — el gate de RRHH cortó el turno: no hay respuesta que
        // presentar, hay un consentimiento que pedir. El sink ya disparó
        // `onHrOptInRequired`; aquí se sale ANTES del camino normal para no
        // rematar con un `onDone` de respuesta vacía ni con el aviso de
        // "respuesta truncada". Espejo exacto de lo que la rama HTTP plana
        // hace desde siempre.
        if (sink.state.hrOptInRequired) {
          track('ai.message.received', { ...analyticsContext, duration_ms: Date.now() - startedAt, path: 'sse', hr_opt_in_required: true });
          return;
        }
        const corrupt = parseErrors >= 3 && !sawDone;
        const truncated = (!sawDone && fullText.length > 0) || corrupt;
        if (truncated) {
          toast.error(
            corrupt && fullText.length === 0
              ? i18n.t('ai.stream.incomplete', 'La respuesta de Aria llegó incompleta. Vuelve a preguntar.')
              : i18n.t('ai.stream.truncated', 'La respuesta de Aria se cortó. Vuelve a preguntar para obtenerla completa.'),
            { duration: 6_000 },
          );
        }
        track('ai.message.received', {
          ...analyticsContext,
          duration_ms: Date.now() - startedAt,
          tools_used: toolsUsed.length,
          has_chart: Boolean(chartData),
          has_pending_action: receivedActions.length > 0,
          path: 'sse',
          truncated,
        });
        onDone?.({ fullText, toolsUsed, agent: agentMeta, chart: chartData, groundingWarning, pendingActions: receivedActions, pendingClarifications: receivedClarifications, path: 'sse', truncated });
      } else {
        // JSON fallback — used when the backend disabled streaming or a
        // middleware (rate limiter, CSRF) intercepted the request.
        if (!res.ok) {
          // Sprint 2 F8 — extract a useful message from the body before
          // falling back to a generic. The previous version threw the raw
          // HTML on 500/502 responses, which the AIAssistant surfaced
          // verbatim to the user.
          const errorText = await res.text();
          if (!isCurrent()) return;
          let userMessage;
          let retryable = res.status === 503; // 503 siempre reintentable
          if (errorText.startsWith('<')) {
            userMessage = `Servidor no disponible (código ${res.status}). Reintenta en un momento.`;
            retryable = true;
          } else {
            try {
              const parsed = JSON.parse(errorText);
              // B2 — el backend marca 'AI_UNAVAILABLE' con retryable:true y un copy amigable.
              if (parsed?.error === 'AI_UNAVAILABLE') {
                userMessage = parsed?.message || 'ARIA no está disponible un momento. Reintenta en unos segundos.';
                retryable = true;
              } else {
                userMessage = parsed?.message || parsed?.error || `Error del servidor (${res.status})`;
                if (typeof parsed?.retryable === 'boolean') retryable = parsed.retryable;
              }
            } catch {
              userMessage = errorText.length > 200 ? errorText.slice(0, 200) + '…' : errorText || `Error del servidor (${res.status})`;
            }
          }
          // La referencia de soporte: del cuerpo (`ref`, sobre de error del backend) o de la cabecera `X-Request-ID`, que CORS
          // expone al frontend desde api#3545.
          let ref = res.headers?.get?.('x-request-id') || undefined;
          try { ref = JSON.parse(errorText)?.ref || ref; } catch { /* cuerpo no JSON: vale la cabecera */ }
          const err = new Error(conReferencia(userMessage, ref));
          err.status = res.status;
          err.retryable = retryable;
          throw err;
        }
        const response = await res.json();
        if (!isCurrent()) return;

        // History-load request — parent handles the side effect (setting
        // initial messages). We don't emit a "message.received" event for
        // these because they are a bulk load, not a reply.
        if (response.history) {
          onDone?.({ history: response.history, path: 'history' });
          return;
        }

        // XC-AI-013 — HR AI opt-in gate. The chatHandler pre-flight short-circuits
        // with { hrOptInRequired: true, response: '' } (HTTP 200, no LLM call) when
        // the query needs an HR tool and the user hasn't accepted the EU AI Act
        // disclaimer. Surface the consent modal instead of rendering the empty
        // body as "no pude procesar". The compact AIPanel widget already does this.
        if (response.hrOptInRequired) {
          onHrOptInRequired?.();
          return;
        }

        let aiResponse = response.response || response.result || response.message || response;
        if (typeof aiResponse === 'object') {
          aiResponse = aiResponse.content || aiResponse.text || JSON.stringify(aiResponse);
        }
        if (response.conversationId) onConversationId?.(response.conversationId);
        if (response.usage) onUsage?.(response.usage);

        const toolsUsed = Array.isArray(response.toolsUsed) ? response.toolsUsed : [];
        const chartData = response.chart || null;
        if (chartData) onChart?.(chartData);

        // Animate word by word so the response doesn't appear all at once
        const words = aiResponse.split(' ');
        let accumulated = '';
        for (const word of words) {
          accumulated += (accumulated ? ' ' : '') + word;
          onText?.(word + (accumulated === aiResponse ? '' : ' '), accumulated);
          await new Promise(r => setTimeout(r, 18));
          if (!isCurrent()) return;
        }

        track('ai.message.received', {
          ...analyticsContext,
          duration_ms: Date.now() - startedAt,
          tools_used: toolsUsed.length,
          has_chart: Boolean(chartData),
          path: 'json',
        });
        onDone?.({ fullText: aiResponse, toolsUsed, chart: chartData, pendingActions: [], path: 'json' });
      }
    } catch (error) {
      if (!isCurrent()) return;
      clearTimeout(globalTimeoutId);
      clearSlowToast();
      const isTimeout = error?.name === 'AbortError';
      track('ai.error', {
        ...analyticsContext,
        duration_ms: Date.now() - startedAt,
        error_code: isTimeout ? 'timeout' : (error?.code || error?.status || 'unknown'),
        message: String(error?.message || '').slice(0, 200),
      });
      // B2 — un timeout o un 503 transitorio son reintentables: el componente
      // ofrece "Reintentar" para reenviar la última pregunta sin reescribirla.
      onError?.({ isTimeout, message: error?.message, status: error?.status, retryable: error?.retryable === true || isTimeout });
    } finally {
      clearTimeout(globalTimeoutId);
      clearTimeout(interChunkTimeout);
      clearSlowToast();
      if (isCurrent()) {
        readerRef.current = null;
        abortRef.current = null;
        setIsStreaming(false);
      }
    }
  }, []);

  return { stream, abort, isStreaming };
}
